import { Typography } from "@saviynt/design-system";
import { FormattedMessage } from "react-intl";
import { SaviyntLogo } from "@repo/ui";

interface LoginHeaderProps {
  theme: string;
  step:
    | "username"
    | "password"
    | "forgotPassword"
    | "forgotPasswordConfirm"
    | "forgotPasswordSuccess"
    | "resetPassword"
    | "setupMfa"
    | "mfa"
    | "success";
}

export default function LoginHeader({ theme, step }: LoginHeaderProps) {
  const getHeading = () => {
    switch (step) {
      case "username":
        return (
          <FormattedMessage
            id="MuvDab"
            defaultMessage="Login faster and safer to your account."
            description="Login page title"
          />
        );
      case "password":
        return (
          <FormattedMessage
            id="8qYVa4"
            defaultMessage="Enter your password"
            description="Password step heading"
          />
        );
      case "forgotPassword":
        return (
          <FormattedMessage
            id="SBqN/7"
            defaultMessage="Forgot Your Password?"
            description="Forgot password heading"
          />
        );
      case "forgotPasswordConfirm":
        return (
          <FormattedMessage
            id="DzLE3a"
            defaultMessage="Reset Your Password"
            description="Forgot password confirm heading"
          />
        );
      case "forgotPasswordSuccess":
        return (
          <FormattedMessage
            id="eoXKCp"
            defaultMessage="Password Reset Successful"
            description="Forgot password success heading"
          />
        );
      case "resetPassword":
        return (
          <FormattedMessage
            id="0Fp6+S"
            defaultMessage="Reset your password"
            description="Reset password subtitle"
          />
        );
      case "setupMfa":
        return (
          <FormattedMessage
            id="axKQq0"
            defaultMessage="Setup Multi-Factor Authentication"
            description="Setup MFA step heading"
          />
        );
      case "mfa":
        return (
          <FormattedMessage
            id="6vtiPj"
            defaultMessage="Verify your Identity"
            description="MFA step heading"
          />
        );
      case "success":
        return (
          <FormattedMessage
            id="KIkqcW"
            defaultMessage="You're all set!"
            description="MFA success step heading"
          />
        );
    }
  };

  const getSubheading = () => {
    switch (step) {
      case "username":
        return (
          <FormattedMessage
            id="oUb4eh"
            defaultMessage="Next time we'll remember how you'd like to sign in."
            description="Login page subtitle"
          />
        );
      case "forgotPassword":
        return (
          <FormattedMessage
            id="XbpJvm"
            defaultMessage="Provide your email address and we will send you instructions to reset your password."
            description="Forgot password subtitle"
          />
        );
      case "forgotPasswordConfirm":
        return (
          <FormattedMessage
            id="PVXF/0"
            defaultMessage="Enter the passcode sent to your email and set a new password."
            description="Forgot password confirm subtitle"
          />
        );
      case "forgotPasswordSuccess":
        return (
          <FormattedMessage
            id="CvlvEw"
            defaultMessage="Your password has been reset. You can now log in with your new password."
            description="Forgot password success subtitle"
          />
        );
      case "resetPassword":
        return (
          <FormattedMessage
            id="MIV6i2"
            defaultMessage="This is your first time logging in with a temporary password. Please reset your password."
            description="Reset password step heading"
          />
        );
      case "setupMfa":
        return (
          <FormattedMessage
            id="yj9wfi"
            defaultMessage="To connect your authenticator app and start using time-based one-time passcodes (TOTP), scan this code."
            description="Setup MFA subtitle"
          />
        );
      case "mfa":
        return (
          <FormattedMessage
            id="Ls8N7z"
            defaultMessage="Open your authenticator and enter the passcode to verify your identity."
            description="MFA prompt text"
          />
        );
      case "success":
        return (
          <FormattedMessage
            id="YRHHhB"
            defaultMessage="Your MFA enrollment is complete."
            description="MFA success step subtitle"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="text-center flex flex-col justify-start items-center">
      <SaviyntLogo
        variant={theme === "dark" ? "dark" : "light"}
        className="h-12 mb-8"
      />
      <div className="flex flex-col gap-2">
        <Typography kind="h3">{getHeading()}</Typography>
        {getSubheading() && (
          <Typography kind="body3">{getSubheading()}</Typography>
        )}
      </div>
    </div>
  );
}
